import { type ReactNode, useRef, useEffect, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

export interface Tab {
  id: string;
  label: string;
  content: ReactNode;
  disabled?: boolean;
}

interface TabsProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
}

export default function Tabs({ tabs, activeTab, onTabChange }: TabsProps) {
  const navRef = useRef<HTMLElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Detectar mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Verificar scrollability
  const checkScrollability = () => {
    // Função mantida para uso futuro
  };

  // Scroll para a tab ativa quando mudar
  useEffect(() => {
    if (navRef.current && isMobile) {
      const activeButton = navRef.current.querySelector(`[data-tab-id="${activeTab}"]`) as HTMLElement;
      if (activeButton) {
        const containerRect = navRef.current.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();
        const scrollPosition = navRef.current.scrollLeft + (buttonRect.left - containerRect.left) - (containerRect.width / 2) + (buttonRect.width / 2);
        
        navRef.current.scrollTo({
          left: scrollPosition,
          behavior: 'smooth',
        });
      }
    }
    checkScrollability();
  }, [activeTab, isMobile]);

  // Verificar scrollability ao scrollar
  useEffect(() => {
    const handleScroll = () => {
      checkScrollability();
    };

    const nav = navRef.current;
    if (nav) {
      nav.addEventListener('scroll', handleScroll);
      checkScrollability();
      return () => nav.removeEventListener('scroll', handleScroll);
    }
  }, [isMobile]);

  // Handlers para arrastar no mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    if (isMobile && navRef.current) {
      setIsDragging(true);
      setDragStartX(e.touches[0].clientX);
      setScrollLeft(navRef.current.scrollLeft);
      navRef.current.style.scrollBehavior = 'auto';
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isDragging && navRef.current && isMobile) {
      e.preventDefault();
      const deltaX = e.touches[0].clientX - dragStartX;
      navRef.current.scrollLeft = scrollLeft - deltaX;
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (navRef.current) {
      navRef.current.style.scrollBehavior = 'smooth';
    }
  };

  // Handlers para mouse (mobile - arrastar)
  const handleMouseDown = (e: React.MouseEvent) => {
    if (isMobile && navRef.current && e.button === 0) {
      setIsDragging(true);
      setDragStartX(e.clientX);
      setScrollLeft(navRef.current.scrollLeft);
      navRef.current.style.scrollBehavior = 'auto';
      navRef.current.style.cursor = 'grabbing';
      e.preventDefault();
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && navRef.current && isMobile) {
      const deltaX = e.clientX - dragStartX;
      navRef.current.scrollLeft = scrollLeft - deltaX;
      checkScrollability();
    }
  };

  const handleMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
      if (navRef.current) {
        navRef.current.style.scrollBehavior = 'smooth';
        navRef.current.style.cursor = 'grab';
      }
    }
  };

  const activeTabIndex = tabs.findIndex(tab => tab.id === activeTab);
  const goToPreviousTab = () => {
    const previousTab = tabs[activeTabIndex - 1];
    if (previousTab && !previousTab.disabled) {
      onTabChange(previousTab.id);
    }
  };

  const goToNextTab = () => {
    const nextTab = tabs[activeTabIndex + 1];
    if (nextTab && !nextTab.disabled) {
      onTabChange(nextTab.id);
    }
  };

  return (
    <div>
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 relative">
        {/* Botões de navegação - Mobile */}
        {isMobile && (
          <div className="flex items-center">
            <button
              onClick={goToPreviousTab}
              disabled={activeTabIndex === 0}
              className="p-2 text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
              aria-label="Tab anterior"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>

            <div className="flex-1 relative overflow-hidden">
              <nav
                ref={navRef}
                className="flex space-x-4 md:space-x-8 overflow-x-auto scrollbar-hide"
                aria-label="Tabs"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                style={{
                  cursor: isMobile ? (isDragging ? 'grabbing' : 'grab') : 'default',
                  userSelect: isMobile ? 'none' : 'auto',
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
              >
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    data-tab-id={tab.id}
                    onClick={() => !tab.disabled && onTabChange(tab.id)}
                    disabled={tab.disabled}
                    className={`
                      py-4 px-2 md:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0
                      ${
                        activeTab === tab.id
                          ? 'border-green-600 text-green-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                      ${tab.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            <button
              onClick={goToNextTab}
              disabled={activeTabIndex === tabs.length - 1}
              className="p-2 text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
              aria-label="Próxima tab"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Desktop - Layout original */}
        {!isMobile && (
          <nav className="flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => !tab.disabled && onTabChange(tab.id)}
                disabled={tab.disabled}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                  ${
                    activeTab === tab.id
                      ? 'border-green-600 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                  ${tab.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        )}
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {tabs.find((tab) => tab.id === activeTab)?.content}
      </div>
    </div>
  );
}

